import React, { useEffect, useState } from 'react';
import AdminSidebar from '../../components/admin/AdminSidebar';
import AdminHeader from '../../components/admin/AdminHeader';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { UserIcon, MailIcon, PhoneIcon, KeyIcon, ShieldIcon, BellIcon, ClockIcon, SaveIcon, RefreshCwIcon, EyeIcon, EyeOffIcon, CheckIcon, XIcon, AlertCircleIcon, ChevronDownIcon, LogOutIcon, LockIcon, UnlockIcon, CheckCircleIcon, XCircleIcon, SmartphoneIcon, CalendarIcon, BuildingIcon, MapPinIcon, BriefcaseIcon, FileTextIcon, ClipboardIcon, SettingsIcon, InfoIcon, UploadIcon, CameraIcon, TrashIcon, PlusIcon, MoonIcon, SunIcon, GlobeIcon, BellOffIcon, ArrowRightIcon, AlertTriangleIcon, HelpCircleIcon } from 'lucide-react';
interface AdminProfile {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: string;
  mdaId?: string;
  mdaName?: string;
  profileImage: string;
  jobTitle: string;
  department?: string;
  dateJoined: string;
  lastLogin: string;
  lastPasswordChange: string;
  twoFactorEnabled: boolean;
  notificationsEnabled: boolean;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  bio?: string;
  permissions: string[];
  darkModeEnabled?: boolean;
  language?: string;
  timeZone?: string;
}
const AdminProfile: React.FC = () => {
  const {
    admin
  } = useAdminAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [profile, setProfile] = useState<AdminProfile | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [activeTab, setActiveTab] = useState('personal');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showTwoFactorModal, setShowTwoFactorModal] = useState(false);
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [showImageUploadModal, setShowImageUploadModal] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [passwordValues, setPasswordValues] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordVisibility, setPasswordVisibility] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false
  });
  const [newProfileImage, setNewProfileImage] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [editedProfile, setEditedProfile] = useState<Partial<AdminProfile>>({});
  // Get admin auth context
  const {
    hasPermission,
    isSuperAdmin,
    getMdaId,
    getMdaName,
    logout
  } = useAdminAuth();
  // Toggle sidebar
  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };
  // Toggle dropdown
  const toggleDropdown = (dropdown: string) => {
    if (activeDropdown === dropdown) {
      setActiveDropdown(null);
    } else {
      setActiveDropdown(dropdown);
    }
  };
  // Toggle password visibility
  const togglePasswordVisibility = (field: 'currentPassword' | 'newPassword' | 'confirmPassword') => {
    setPasswordVisibility({
      ...passwordVisibility,
      [field]: !passwordVisibility[field]
    });
  };
  // Load admin profile data
  useEffect(() => {
    // Simulate API call to fetch profile data
    setTimeout(() => {
      const mockProfile: AdminProfile = {
        id: 'admin-123',
        firstName: 'John',
        lastName: 'Doe',
        email: isSuperAdmin() ? 'admin@govservices.gov.ng' : `admin@${getMdaName()?.toLowerCase().replace(/\s+/g, '')}.gov.ng`,
        phone: '+234 800 123 4567',
        role: isSuperAdmin() ? 'Super Administrator' : 'MDA Administrator',
        profileImage: 'https://randomuser.me/api/portraits/men/32.jpg',
        jobTitle: isSuperAdmin() ? 'Chief System Administrator' : 'MDA Administrator',
        dateJoined: '2022-05-15T10:30:00Z',
        lastLogin: '2023-07-21T08:45:23Z',
        lastPasswordChange: '2023-06-10T14:20:15Z',
        twoFactorEnabled: true,
        notificationsEnabled: true,
        permissions: isSuperAdmin() ? ['users:read', 'users:write', 'mdas:read', 'mdas:write', 'applications:read', 'applications:write', 'services:read', 'services:width', 'payments:read', 'payments:write', 'reports:read', 'reports:write', 'settings:read', 'settings:write'] : ['users:read', 'applications:read', 'applications:write', 'services:read', 'services:write', 'payments:read', 'reports:read', 'settings:read'],
        darkModeEnabled: false,
        language: 'English',
        timeZone: 'Africa/Lagos'
      };
      if (!isSuperAdmin()) {
        mockProfile.mdaId = getMdaId() || 'mda-1';
        mockProfile.mdaName = getMdaName() || 'Ministry of Finance';
        mockProfile.department = 'IT Department';
      }
      setProfile(mockProfile);
      setEditedProfile({});
      setIsLoading(false);
    }, 1000);
  }, [isSuperAdmin, getMdaId, getMdaName]);
  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setEditedProfile({
      ...editedProfile,
      [name]: value
    });
  };
  // Handle toggle change
  const handleToggleChange = (name: string, value: boolean) => {
    setEditedProfile({
      ...editedProfile,
      [name]: value
    });
  };
  // Handle password change
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      value
    } = e.target;
    setPasswordValues({
      ...passwordValues,
      [name]: value
    });
  };
  // Handle profile image change
  const handleProfileImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setNewProfileImage(file);
      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };
  // Save profile changes
  const saveProfileChanges = () => {
    setIsSaving(true);
    setErrorMessage(null);
    // Simulate API call
    setTimeout(() => {
      if (profile) {
        // Update profile with edited values
        const updatedProfile = {
          ...profile,
          ...editedProfile
        };
        setProfile(updatedProfile);
        setEditedProfile({});
        setEditMode(false);
        setIsSaving(false);
        setSuccessMessage('Profile updated successfully');
        // Clear success message after 3 seconds
        setTimeout(() => {
          setSuccessMessage(null);
        }, 3000);
      }
    }, 1500);
  };
  // Change password
  const changePassword = () => {
    setIsSaving(true);
    setErrorMessage(null);
    // Validate passwords
    if (passwordValues.newPassword !== passwordValues.confirmPassword) {
      setErrorMessage('New password and confirmation do not match');
      setIsSaving(false);
      return;
    }
    if (passwordValues.newPassword.length < 8) {
      setErrorMessage('Password must be at least 8 characters long');
      setIsSaving(false);
      return;
    }
    // Simulate API call
    setTimeout(() => {
      // Reset form
      setPasswordValues({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setIsSaving(false);
      setShowPasswordModal(false);
      setSuccessMessage('Password changed successfully');
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
      // Update last password change date
      if (profile) {
        const updatedProfile = {
          ...profile,
          lastPasswordChange: new Date().toISOString()
        };
        setProfile(updatedProfile);
      }
    }, 1500);
  };
  // Toggle two-factor authentication
  const toggleTwoFactor = (enable: boolean) => {
    setIsSaving(true);
    // Simulate API call
    setTimeout(() => {
      if (profile) {
        const updatedProfile = {
          ...profile,
          twoFactorEnabled: enable
        };
        setProfile(updatedProfile);
        setIsSaving(false);
        setShowTwoFactorModal(false);
        setSuccessMessage(`Two-factor authentication ${enable ? 'enabled' : 'disabled'} successfully`);
        // Clear success message after 3 seconds
        setTimeout(() => {
          setSuccessMessage(null);
        }, 3000);
      }
    }, 1500);
  };
  // Upload profile image
  const uploadProfileImage = () => {
    setIsSaving(true);
    // Simulate API call
    setTimeout(() => {
      if (profile && previewUrl) {
        const updatedProfile = {
          ...profile,
          profileImage: previewUrl
        };
        setProfile(updatedProfile);
        setIsSaving(false);
        setShowImageUploadModal(false);
        setNewProfileImage(null);
        setPreviewUrl(null);
        setSuccessMessage('Profile image updated successfully');
        // Clear success message after 3 seconds
        setTimeout(() => {
          setSuccessMessage(null);
        }, 3000);
      }
    }, 1500);
  };
  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-NG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };
  // Format time
  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-NG', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  // Format date and time
  const formatDateTime = (dateString: string) => {
    return `${formatDate(dateString)} at ${formatTime(dateString)}`;
  };
  // Handle logout
  const handleLogout = () => {
    setShowLogoutModal(false);
    logout();
  };
  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center bg-gray-100" data-id="element-821">
        <div className="flex flex-col items-center" data-id="element-822">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mb-4" data-id="element-823"></div>
          <p className="text-gray-600" data-id="element-824">Loading profile...</p>
        </div>
      </div>;
  }
  return <div className="flex h-screen bg-gray-100" data-id="element-825">
      {/* Sidebar - hidden on mobile unless toggled */}
      <div className={`${isSidebarOpen ? 'block' : 'hidden'} lg:block fixed inset-y-0 left-0 z-50 lg:relative lg:z-0`} data-id="element-826">
        <AdminSidebar data-id="element-827" />
      </div>
      {/* Backdrop for mobile sidebar */}
      {isSidebarOpen && <div className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden" onClick={() => setIsSidebarOpen(false)} data-id="element-828"></div>}
      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden" data-id="element-829">
        <AdminHeader toggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} data-id="element-830" />
        <main className="flex-1 overflow-y-auto p-4 lg:p-6" data-id="element-831">
          {/* Page Header */}
          <div className="mb-6" data-id="element-832">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-833">My Profile</h1>
            <p className="text-gray-600 mt-1" data-id="element-834">
              Manage your account information and settings
            </p>
          </div>
          {/* Success Message */}
          {successMessage && <div className="mb-6 bg-green-50 border-l-4 border-green-500 p-4 rounded-md" data-id="element-835">
              <div className="flex items-center" data-id="element-836">
                <CheckCircleIcon size={20} className="text-green-500 mr-2" data-id="element-837" />
                <p className="text-sm text-green-700" data-id="element-838">{successMessage}</p>
              </div>
            </div>}
          {/* Error Message */}
          {errorMessage && <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4 rounded-md" data-id="element-839">
              <div className="flex items-center" data-id="element-840">
                <XCircleIcon size={20} className="text-red-500 mr-2" data-id="element-841" />
                <p className="text-sm text-red-700" data-id="element-842">{errorMessage}</p>
              </div>
            </div>}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-843">
            {/* Profile Sidebar */}
            <div className="lg:col-span-1 space-y-6" data-id="element-844">
              {/* Profile Card */}
              <div className="bg-white rounded-lg shadow overflow-hidden" data-id="element-845">
                <div className="bg-green-600 h-24 relative" data-id="element-846">
                  <div className="absolute -bottom-12 left-6" data-id="element-847">
                    <div className="relative" data-id="element-848">
                      <img src={profile?.profileImage} alt={`${profile?.firstName} ${profile?.lastName}`} className="w-24 h-24 rounded-full border-4 border-white object-cover" data-id="element-849" />
                      <button onClick={() => setShowImageUploadModal(true)} className="absolute bottom-0 right-0 bg-gray-100 rounded-full p-1 shadow-md hover:bg-gray-200" title="Change profile picture" data-id="element-850">
                        <CameraIcon size={16} className="text-gray-600" data-id="element-851" />
                      </button>
                    </div>
                  </div>
                </div>
                <div className="pt-14 pb-6 px-6" data-id="element-852">
                  <h2 className="text-xl font-bold text-gray-900" data-id="element-853">
                    {profile?.firstName} {profile?.lastName}
                  </h2>
                  <p className="text-sm text-gray-500 mt-1" data-id="element-854">
                    {profile?.role}
                    {profile?.mdaName && ` at ${profile.mdaName}`}
                  </p>
                  <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-855">
                    <div className="flex items-center text-sm text-gray-600 mb-3" data-id="element-856">
                      <MailIcon size={16} className="mr-3 text-gray-400" data-id="element-857" />
                      {profile?.email}
                    </div>
                    <div className="flex items-center text-sm text-gray-600 mb-3" data-id="element-858">
                      <PhoneIcon size={16} className="mr-3 text-gray-400" data-id="element-859" />
                      {profile?.phone}
                    </div>
                    <div className="flex items-center text-sm text-gray-600 mb-3" data-id="element-860">
                      <BriefcaseIcon size={16} className="mr-3 text-gray-400" data-id="element-861" />
                      {profile?.jobTitle}
                    </div>
                    {profile?.department && <div className="flex items-center text-sm text-gray-600 mb-3" data-id="element-862">
                        <BuildingIcon size={16} className="mr-3 text-gray-400" data-id="element-863" />
                        {profile.department}
                      </div>}
                  </div>
                  <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-864">
                    <div className="flex items-center text-sm text-gray-600 mb-3" data-id="element-865">
                      <CalendarIcon size={16} className="mr-3 text-gray-400" data-id="element-866" />
                      <div data-id="element-867">
                        <span className="block text-xs text-gray-500" data-id="element-868">
                          Joined
                        </span>
                        {formatDate(profile?.dateJoined || '')}
                      </div>
                    </div>
                    <div className="flex items-center text-sm text-gray-600 mb-3" data-id="element-869">
                      <ClockIcon size={16} className="mr-3 text-gray-400" data-id="element-870" />
                      <div data-id="element-871">
                        <span className="block text-xs text-gray-500" data-id="element-872">
                          Last login
                        </span>
                        {formatDateTime(profile?.lastLogin || '')}
                      </div>
                    </div>
                  </div>
                  {/* Quick Actions */}
                  <div className="mt-6 space-y-2" data-id="element-873">
                    <button onClick={() => setShowPasswordModal(true)} className="w-full flex items-center justify-between text-sm px-3 py-2 bg-gray-50 text-gray-700 rounded-md hover:bg-gray-100" data-id="element-874">
                      <span className="flex items-center" data-id="element-875">
                        <KeyIcon size={16} className="mr-2" data-id="element-876" />
                        Change Password
                      </span>
                      <ArrowRightIcon size={16} data-id="element-877" />
                    </button>
                    <button onClick={() => setShowTwoFactorModal(true)} className="w-full flex items-center justify-between text-sm px-3 py-2 bg-gray-50 text-gray-700 rounded-md hover:bg-gray-100" data-id="element-878">
                      <span className="flex items-center" data-id="element-879">
                        <ShieldIcon size={16} className="mr-2" data-id="element-880" />
                        Two-Factor Authentication
                      </span>
                      <ArrowRightIcon size={16} data-id="element-881" />
                    </button>
                    <button onClick={() => setShowLogoutModal(true)} className="w-full flex items-center justify-between text-sm px-3 py-2 bg-red-50 text-red-700 rounded-md hover:bg-red-100" data-id="element-882">
                      <span className="flex items-center" data-id="element-883">
                        <LogOutIcon size={16} className="mr-2" data-id="element-884" />
                        Logout
                      </span>
                      <ArrowRightIcon size={16} data-id="element-885" />
                    </button>
                  </div>
                </div>
              </div>
              {/* Security Status */}
              <div className="bg-white rounded-lg shadow p-6" data-id="element-886">
                <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-887">
                  Security Status
                </h3>
                <div className="space-y-4" data-id="element-888">
                  <div className="flex items-center justify-between" data-id="element-889">
                    <div className="flex items-center" data-id="element-890">
                      <KeyIcon size={16} className="mr-3 text-gray-400" data-id="element-891" />
                      <div data-id="element-892">
                        <span className="block text-sm text-gray-900" data-id="element-893">
                          Password
                        </span>
                        <span className="block text-xs text-gray-500" data-id="element-894">
                          Last changed{' '}
                          {formatDate(profile?.lastPasswordChange || '')}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center" data-id="element-895">
                      {new Date(profile?.lastPasswordChange || '').getTime() > Date.now() - 90 * 24 * 60 * 60 * 1000 ? <span className="flex items-center text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full" data-id="element-896">
                          <CheckCircleIcon size={12} className="mr-1" data-id="element-897" />
                          Good
                        </span> : <span className="flex items-center text-xs text-yellow-600 bg-yellow-50 px-2 py-1 rounded-full" data-id="element-898">
                          <AlertCircleIcon size={12} className="mr-1" data-id="element-899" />
                          Update recommended
                        </span>}
                    </div>
                  </div>
                  <div className="flex items-center justify-between" data-id="element-900">
                    <div className="flex items-center" data-id="element-901">
                      <ShieldIcon size={16} className="mr-3 text-gray-400" data-id="element-902" />
                      <div data-id="element-903">
                        <span className="block text-sm text-gray-900" data-id="element-904">
                          Two-Factor Authentication
                        </span>
                        <span className="block text-xs text-gray-500" data-id="element-905">
                          {profile?.twoFactorEnabled ? 'Enabled' : 'Disabled'}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center" data-id="element-906">
                      {profile?.twoFactorEnabled ? <span className="flex items-center text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full" data-id="element-907">
                          <CheckCircleIcon size={12} className="mr-1" data-id="element-908" />
                          Enabled
                        </span> : <span className="flex items-center text-xs text-red-600 bg-red-50 px-2 py-1 rounded-full" data-id="element-909">
                          <XCircleIcon size={12} className="mr-1" data-id="element-910" />
                          Disabled
                        </span>}
                    </div>
                  </div>
                  <div className="flex items-center justify-between" data-id="element-911">
                    <div className="flex items-center" data-id="element-912">
                      <BellIcon size={16} className="mr-3 text-gray-400" data-id="element-913" />
                      <div data-id="element-914">
                        <span className="block text-sm text-gray-900" data-id="element-915">
                          Notifications
                        </span>
                        <span className="block text-xs text-gray-500" data-id="element-916">
                          {profile?.notificationsEnabled ? 'Enabled' : 'Disabled'}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center" data-id="element-917">
                      {profile?.notificationsEnabled ? <span className="flex items-center text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full" data-id="element-918">
                          <CheckCircleIcon size={12} className="mr-1" data-id="element-919" />
                          Enabled
                        </span> : <span className="flex items-center text-xs text-gray-600 bg-gray-50 px-2 py-1 rounded-full" data-id="element-920">
                          <XCircleIcon size={12} className="mr-1" data-id="element-921" />
                          Disabled
                        </span>}
                    </div>
                  </div>
                </div>
              </div>
              {/* Help Box */}
              <div className="bg-white rounded-lg shadow p-4" data-id="element-922">
                <div className="flex items-start" data-id="element-923">
                  <div className="flex-shrink-0" data-id="element-924">
                    <HelpCircleIcon size={20} className="text-blue-500" data-id="element-925" />
                  </div>
                  <div className="ml-3" data-id="element-926">
                    <h3 className="text-sm font-medium text-gray-900" data-id="element-927">
                      Need help?
                    </h3>
                    <p className="mt-1 text-sm text-gray-500" data-id="element-928">
                      If you need assistance with your account settings, please
                      contact our support team.
                    </p>
                    <a href="#" className="mt-2 text-sm text-blue-600 hover:text-blue-500 flex items-center" data-id="element-929">
                      Contact Support
                    </a>
                  </div>
                </div>
              </div>
            </div>
            {/* Profile Content */}
            <div className="lg:col-span-2" data-id="element-930">
              <div className="bg-white rounded-lg shadow" data-id="element-931">
                {/* Tabs */}
                <div className="border-b border-gray-200" data-id="element-932">
                  <nav className="flex -mb-px" data-id="element-933">
                    <button className={`py-4 px-6 border-b-2 text-sm font-medium ${activeTab === 'personal' ? 'border-green-500 text-green-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} onClick={() => setActiveTab('personal')} data-id="element-934">
                      Personal Information
                    </button>
                    <button className={`py-4 px-6 border-b-2 text-sm font-medium ${activeTab === 'security' ? 'border-green-500 text-green-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} onClick={() => setActiveTab('security')} data-id="element-935">
                      Security
                    </button>
                    <button className={`py-4 px-6 border-b-2 text-sm font-medium ${activeTab === 'preferences' ? 'border-green-500 text-green-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} onClick={() => setActiveTab('preferences')} data-id="element-936">
                      Preferences
                    </button>
                  </nav>
                </div>
                {/* Personal Information Tab */}
                {activeTab === 'personal' && <div className="p-6" data-id="element-937">
                    <div className="flex justify-between items-center mb-6" data-id="element-938">
                      <h3 className="text-lg font-medium text-gray-900" data-id="element-939">
                        Personal Information
                      </h3>
                      <button onClick={() => setEditMode(!editMode)} className={`text-sm px-4 py-2 rounded-md ${editMode ? 'bg-gray-100 text-gray-600' : 'bg-green-50 text-green-600'}`} data-id="element-940">
                        {editMode ? 'Cancel' : 'Edit Profile'}
                      </button>
                    </div>
                    <div className="space-y-6" data-id="element-941">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-942">
                        <div data-id="element-943">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-944">
                            First Name
                          </label>
                          {editMode ? <input type="text" name="firstName" value={editedProfile.firstName !== undefined ? editedProfile.firstName : profile?.firstName} onChange={handleInputChange} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm" data-id="element-945" /> : <p className="text-sm text-gray-900" data-id="element-946">
                              {profile?.firstName}
                            </p>}
                        </div>
                        <div data-id="element-947">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-948">
                            Last Name
                          </label>
                          {editMode ? <input type="text" name="lastName" value={editedProfile.lastName !== undefined ? editedProfile.lastName : profile?.lastName} onChange={handleInputChange} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm" data-id="element-949" /> : <p className="text-sm text-gray-900" data-id="element-950">
                              {profile?.lastName}
                            </p>}
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-951">
                        <div data-id="element-952">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-953">
                            Email Address
                          </label>
                          <p className="text-sm text-gray-900" data-id="element-954">
                            {profile?.email}
                          </p>
                          <p className="text-xs text-gray-500 mt-1" data-id="element-955">
                            Email cannot be changed
                          </p>
                        </div>
                        <div data-id="element-956">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-957">
                            Phone Number
                          </label>
                          {editMode ? <input type="text" name="phone" value={editedProfile.phone !== undefined ? editedProfile.phone : profile?.phone} onChange={handleInputChange} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm" data-id="element-958" /> : <p className="text-sm text-gray-900" data-id="element-959">
                              {profile?.phone}
                            </p>}
                        </div>
                      </div>
                      <div data-id="element-960">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-961">
                          Job Title
                        </label>
                        {editMode ? <input type="text" name="jobTitle" value={editedProfile.jobTitle !== undefined ? editedProfile.jobTitle : profile?.jobTitle} onChange={handleInputChange} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm" data-id="element-962" /> : <p className="text-sm text-gray-900" data-id="element-963">
                            {profile?.jobTitle}
                          </p>}
                      </div>
                      {profile?.department && <div data-id="element-964">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-965">
                            Department
                          </label>
                          {editMode ? <input type="text" name="department" value={editedProfile.department !== undefined ? editedProfile.department : profile?.department} onChange={handleInputChange} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm" data-id="element-966" /> : <p className="text-sm text-gray-900" data-id="element-967">
                              {profile?.department}
                            </p>}
                        </div>}
                      <div data-id="element-968">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-969">
                          Bio
                        </label>
                        {editMode ? <textarea name="bio" rows={4} value={editedProfile.bio !== undefined ? editedProfile.bio : profile?.bio || ''} onChange={handleInputChange} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm" placeholder="Tell us about yourself" data-id="element-970" /> : <p className="text-sm text-gray-900" data-id="element-971">
                            {profile?.bio || 'No bio provided'}
                          </p>}
                      </div>
                      {editMode && <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-972">
                          <div data-id="element-973">
                            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-974">
                              Address
                            </label>
                            <input type="text" name="address" value={editedProfile.address !== undefined ? editedProfile.address : profile?.address || ''} onChange={handleInputChange} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm" placeholder="Your address" data-id="element-975" />
                          </div>
                          <div data-id="element-976">
                            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-977">
                              City
                            </label>
                            <input type="text" name="city" value={editedProfile.city !== undefined ? editedProfile.city : profile?.city || ''} onChange={handleInputChange} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm" placeholder="Your city" data-id="element-978" />
                          </div>
                        </div>}
                      {editMode && <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-979">
                          <div data-id="element-980">
                            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-981">
                              State
                            </label>
                            <input type="text" name="state" value={editedProfile.state !== undefined ? editedProfile.state : profile?.state || ''} onChange={handleInputChange} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm" placeholder="Your state" data-id="element-982" />
                          </div>
                          <div data-id="element-983">
                            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-984">
                              Country
                            </label>
                            <input type="text" name="country" value={editedProfile.country !== undefined ? editedProfile.country : profile?.country || ''} onChange={handleInputChange} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm" placeholder="Your country" defaultValue="Nigeria" data-id="element-985" />
                          </div>
                        </div>}
                      {editMode && <div className="flex justify-end" data-id="element-986">
                          <button onClick={saveProfileChanges} disabled={isSaving} className="flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500" data-id="element-987">
                            {isSaving ? <>
                                <RefreshCwIcon size={16} className="mr-2 animate-spin" data-id="element-988" />
                                Saving...
                              </> : <>
                                <SaveIcon size={16} className="mr-2" data-id="element-989" />
                                Save Changes
                              </>}
                          </button>
                        </div>}
                    </div>
                  </div>}
                {/* Security Tab */}
                {activeTab === 'security' && <div className="p-6" data-id="element-990">
                    <h3 className="text-lg font-medium text-gray-900 mb-6" data-id="element-991">
                      Security Settings
                    </h3>
                    <div className="space-y-6" data-id="element-992">
                      {/* Password */}
                      <div className="bg-gray-50 p-4 rounded-lg" data-id="element-993">
                        <div className="flex justify-between items-start" data-id="element-994">
                          <div className="flex items-start" data-id="element-995">
                            <div className="mt-0.5" data-id="element-996">
                              <KeyIcon size={20} className="text-gray-500" data-id="element-997" />
                            </div>
                            <div className="ml-3" data-id="element-998">
                              <h4 className="text-sm font-medium text-gray-900" data-id="element-999">
                                Password
                              </h4>
                              <p className="text-xs text-gray-500 mt-1" data-id="element-1000">
                                Last changed on{' '}
                                {formatDate(profile?.lastPasswordChange || '')}
                              </p>
                            </div>
                          </div>
                          <button onClick={() => setShowPasswordModal(true)} className="text-xs bg-white border border-gray-300 text-gray-700 px-2.5 py-1.5 rounded-md hover:bg-gray-50" data-id="element-1001">
                            Change
                          </button>
                        </div>
                      </div>
                      {/* Two-Factor Authentication */}
                      <div className="bg-gray-50 p-4 rounded-lg" data-id="element-1002">
                        <div className="flex justify-between items-start" data-id="element-1003">
                          <div className="flex items-start" data-id="element-1004">
                            <div className="mt-0.5" data-id="element-1005">
                              <ShieldIcon size={20} className="text-gray-500" data-id="element-1006" />
                            </div>
                            <div className="ml-3" data-id="element-1007">
                              <h4 className="text-sm font-medium text-gray-900" data-id="element-1008">
                                Two-Factor Authentication
                              </h4>
                              <p className="text-xs text-gray-500 mt-1" data-id="element-1009">
                                {profile?.twoFactorEnabled ? 'Enabled - Your account has an extra layer of security' : 'Disabled - Enable for additional security'}
                              </p>
                            </div>
                          </div>
                          <button onClick={() => setShowTwoFactorModal(true)} className={`text-xs px-2.5 py-1.5 rounded-md ${profile?.twoFactorEnabled ? 'bg-red-50 border border-red-300 text-red-700 hover:bg-red-100' : 'bg-green-50 border border-green-300 text-green-700 hover:bg-green-100'}`} data-id="element-1010">
                            {profile?.twoFactorEnabled ? 'Disable' : 'Enable'}
                          </button>
                        </div>
                      </div>
                      {/* Account Activity */}
                      <div data-id="element-1011">
                        <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-1012">
                          Recent Account Activity
                        </h4>
                        <div className="bg-white border border-gray-200 rounded-md overflow-hidden" data-id="element-1013">
                          <div className="p-4 border-b border-gray-200" data-id="element-1014">
                            <div className="flex items-start" data-id="element-1015">
                              <div className="flex-shrink-0 bg-blue-100 rounded-full p-1" data-id="element-1016">
                                <UserIcon size={16} className="text-blue-600" data-id="element-1017" />
                              </div>
                              <div className="ml-3" data-id="element-1018">
                                <p className="text-sm font-medium text-gray-900" data-id="element-1019">
                                  Successful login
                                </p>
                                <p className="text-xs text-gray-500 mt-1" data-id="element-1020">
                                  {formatDateTime(profile?.lastLogin || '')}
                                </p>
                              </div>
                            </div>
                          </div>
                          <div className="p-4 border-b border-gray-200" data-id="element-1021">
                            <div className="flex items-start" data-id="element-1022">
                              <div className="flex-shrink-0 bg-green-100 rounded-full p-1" data-id="element-1023">
                                <KeyIcon size={16} className="text-green-600" data-id="element-1024" />
                              </div>
                              <div className="ml-3" data-id="element-1025">
                                <p className="text-sm font-medium text-gray-900" data-id="element-1026">
                                  Password changed
                                </p>
                                <p className="text-xs text-gray-500 mt-1" data-id="element-1027">
                                  {formatDateTime(profile?.lastPasswordChange || '')}
                                </p>
                              </div>
                            </div>
                          </div>
                          <div className="p-4" data-id="element-1028">
                            <div className="flex items-start" data-id="element-1029">
                              <div className="flex-shrink-0 bg-purple-100 rounded-full p-1" data-id="element-1030">
                                <SettingsIcon size={16} className="text-purple-600" data-id="element-1031" />
                              </div>
                              <div className="ml-3" data-id="element-1032">
                                <p className="text-sm font-medium text-gray-900" data-id="element-1033">
                                  Profile updated
                                </p>
                                <p className="text-xs text-gray-500 mt-1" data-id="element-1034">
                                  {formatDateTime(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString())}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="mt-2 text-right" data-id="element-1035">
                          <button className="text-xs text-blue-600 hover:text-blue-800" data-id="element-1036">
                            View all activity
                          </button>
                        </div>
                      </div>
                      {/* Sessions */}
                      <div data-id="element-1037">
                        <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-1038">
                          Active Sessions
                        </h4>
                        <div className="bg-white border border-gray-200 rounded-md overflow-hidden" data-id="element-1039">
                          <div className="p-4 border-b border-gray-200" data-id="element-1040">
                            <div className="flex justify-between items-start" data-id="element-1041">
                              <div className="flex items-start" data-id="element-1042">
                                <div className="flex-shrink-0 bg-green-100 rounded-full p-1" data-id="element-1043">
                                  <SmartphoneIcon size={16} className="text-green-600" data-id="element-1044" />
                                </div>
                                <div className="ml-3" data-id="element-1045">
                                  <p className="text-sm font-medium text-gray-900" data-id="element-1046">
                                    Current session
                                  </p>
                                  <p className="text-xs text-gray-500 mt-1" data-id="element-1047">
                                    Lagos, Nigeria • Chrome on Windows
                                  </p>
                                </div>
                              </div>
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-1048">
                                Active now
                              </span>
                            </div>
                          </div>
                          <div className="p-4" data-id="element-1049">
                            <div className="flex justify-between items-start" data-id="element-1050">
                              <div className="flex items-start" data-id="element-1051">
                                <div className="flex-shrink-0 bg-gray-100 rounded-full p-1" data-id="element-1052">
                                  <SmartphoneIcon size={16} className="text-gray-600" data-id="element-1053" />
                                </div>
                                <div className="ml-3" data-id="element-1054">
                                  <p className="text-sm font-medium text-gray-900" data-id="element-1055">
                                    Mobile session
                                  </p>
                                  <p className="text-xs text-gray-500 mt-1" data-id="element-1056">
                                    Lagos, Nigeria • Native App on Android
                                  </p>
                                </div>
                              </div>
                              <button className="text-xs text-red-600 hover:text-red-800" data-id="element-1057">
                                Logout
                              </button>
                            </div>
                          </div>
                        </div>
                        <div className="mt-2 text-right" data-id="element-1058">
                          <button className="text-xs text-red-600 hover:text-red-800" data-id="element-1059">
                            Logout from all devices
                          </button>
                        </div>
                      </div>
                      {/* Account Deletion */}
                      <div className="border-t border-gray-200 pt-6 mt-6" data-id="element-1060">
                        <div className="flex items-start" data-id="element-1061">
                          <div className="flex-shrink-0 bg-red-100 rounded-full p-1" data-id="element-1062">
                            <AlertTriangleIcon size={16} className="text-red-600" data-id="element-1063" />
                          </div>
                          <div className="ml-3" data-id="element-1064">
                            <h4 className="text-sm font-medium text-gray-900" data-id="element-1065">
                              Danger Zone
                            </h4>
                            <p className="text-xs text-gray-500 mt-1" data-id="element-1066">
                              Once you delete your account, there is no going
                              back. Please be certain.
                            </p>
                            <button onClick={() => setShowDeleteAccountModal(true)} className="mt-2 text-xs text-red-600 hover:text-red-800" data-id="element-1067">
                              Delete my account
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>}
                {/* Preferences Tab */}
                {activeTab === 'preferences' && <div className="p-6" data-id="element-1068">
                    <h3 className="text-lg font-medium text-gray-900 mb-6" data-id="element-1069">
                      Preferences
                    </h3>
                    <div className="space-y-6" data-id="element-1070">
                      {/* Notification Preferences */}
                      <div data-id="element-1071">
                        <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-1072">
                          Notification Preferences
                        </h4>
                        <div className="space-y-4" data-id="element-1073">
                          <div className="flex items-center justify-between" data-id="element-1074">
                            <div className="flex items-center" data-id="element-1075">
                              <BellIcon size={16} className="text-gray-400 mr-3" data-id="element-1076" />
                              <div data-id="element-1077">
                                <span className="text-sm text-gray-900" data-id="element-1078">
                                  Email Notifications
                                </span>
                                <p className="text-xs text-gray-500" data-id="element-1079">
                                  Receive notifications via email
                                </p>
                              </div>
                            </div>
                            <div data-id="element-1080">
                              <button onClick={() => handleToggleChange('notificationsEnabled', !profile?.notificationsEnabled)} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 ${editedProfile.notificationsEnabled !== undefined ? editedProfile.notificationsEnabled ? 'bg-green-600' : 'bg-gray-200' : profile?.notificationsEnabled ? 'bg-green-600' : 'bg-gray-200'}`} data-id="element-1081">
                                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${editedProfile.notificationsEnabled !== undefined ? editedProfile.notificationsEnabled ? 'translate-x-6' : 'translate-x-1' : profile?.notificationsEnabled ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-1082" />
                              </button>
                            </div>
                          </div>
                          <div className="flex items-center justify-between" data-id="element-1083">
                            <div className="flex items-center" data-id="element-1084">
                              <BellIcon size={16} className="text-gray-400 mr-3" data-id="element-1085" />
                              <div data-id="element-1086">
                                <span className="text-sm text-gray-900" data-id="element-1087">
                                  In-App Notifications
                                </span>
                                <p className="text-xs text-gray-500" data-id="element-1088">
                                  Receive notifications within the application
                                </p>
                              </div>
                            </div>
                            <div data-id="element-1089">
                              <button className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 bg-green-600`} data-id="element-1090">
                                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform translate-x-6`} data-id="element-1091" />
                              </button>
                            </div>
                          </div>
                          <div className="flex items-center justify-between" data-id="element-1092">
                            <div className="flex items-center" data-id="element-1093">
                              <BellIcon size={16} className="text-gray-400 mr-3" data-id="element-1094" />
                              <div data-id="element-1095">
                                <span className="text-sm text-gray-900" data-id="element-1096">
                                  SMS Notifications
                                </span>
                                <p className="text-xs text-gray-500" data-id="element-1097">
                                  Receive important notifications via SMS
                                </p>
                              </div>
                            </div>
                            <div data-id="element-1098">
                              <button className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 bg-gray-200`} data-id="element-1099">
                                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform translate-x-1`} data-id="element-1100" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                      {/* Appearance */}
                      <div className="pt-6 border-t border-gray-200" data-id="element-1101">
                        <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-1102">
                          Appearance
                        </h4>
                        <div className="space-y-4" data-id="element-1103">
                          <div className="flex items-center justify-between" data-id="element-1104">
                            <div className="flex items-center" data-id="element-1105">
                              <MoonIcon size={16} className="text-gray-400 mr-3" data-id="element-1106" />
                              <div data-id="element-1107">
                                <span className="text-sm text-gray-900" data-id="element-1108">
                                  Dark Mode
                                </span>
                                <p className="text-xs text-gray-500" data-id="element-1109">
                                  Use dark theme for the interface
                                </p>
                              </div>
                            </div>
                            <div data-id="element-1110">
                              <button onClick={() => handleToggleChange('darkModeEnabled', !profile?.darkModeEnabled)} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 ${editedProfile.darkModeEnabled !== undefined ? editedProfile.darkModeEnabled ? 'bg-green-600' : 'bg-gray-200' : profile?.darkModeEnabled ? 'bg-green-600' : 'bg-gray-200'}`} data-id="element-1111">
                                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${editedProfile.darkModeEnabled !== undefined ? editedProfile.darkModeEnabled ? 'translate-x-6' : 'translate-x-1' : profile?.darkModeEnabled ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-1112" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                      {/* Language and Region */}
                      <div className="pt-6 border-t border-gray-200" data-id="element-1113">
                        <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-1114">
                          Language and Region
                        </h4>
                        <div className="space-y-4" data-id="element-1115">
                          <div data-id="element-1116">
                            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1117">
                              Language
                            </label>
                            <select name="language" value={editedProfile.language !== undefined ? editedProfile.language : profile?.language} onChange={handleInputChange} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm" data-id="element-1118">
                              <option value="English" data-id="element-1119">English</option>
                              <option value="Hausa" data-id="element-1120">Hausa</option>
                              <option value="Yoruba" data-id="element-1121">Yoruba</option>
                              <option value="Igbo" data-id="element-1122">Igbo</option>
                              <option value="Pidgin" data-id="element-1123">Pidgin</option>
                            </select>
                          </div>
                          <div data-id="element-1124">
                            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1125">
                              Time Zone
                            </label>
                            <select name="timeZone" value={editedProfile.timeZone !== undefined ? editedProfile.timeZone : profile?.timeZone} onChange={handleInputChange} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm" data-id="element-1126">
                              <option value="Africa/Lagos" data-id="element-1127">Africa/Lagos</option>
                              <option value="UTC" data-id="element-1128">UTC</option>
                              <option value="Europe/London" data-id="element-1129">
                                Europe/London
                              </option>
                              <option value="America/New_York" data-id="element-1130">
                                America/New_York
                              </option>
                            </select>
                          </div>
                        </div>
                      </div>
                      {/* Save Button */}
                      {(editedProfile.notificationsEnabled !== undefined || editedProfile.darkModeEnabled !== undefined || editedProfile.language !== undefined || editedProfile.timeZone !== undefined) && <div className="flex justify-end mt-6" data-id="element-1131">
                          <button onClick={saveProfileChanges} disabled={isSaving} className="flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500" data-id="element-1132">
                            {isSaving ? <>
                                <RefreshCwIcon size={16} className="mr-2 animate-spin" data-id="element-1133" />
                                Saving...
                              </> : <>
                                <SaveIcon size={16} className="mr-2" data-id="element-1134" />
                                Save Changes
                              </>}
                          </button>
                        </div>}
                    </div>
                  </div>}
              </div>
              {/* Permissions Section */}
              <div className="bg-white rounded-lg shadow mt-6 p-6" data-id="element-1135">
                <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-1136">
                  Your Permissions
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-1137">
                  {profile?.permissions.map((permission, index) => <div key={index} className="flex items-center bg-gray-50 p-3 rounded-md" data-id="element-1138">
                      <CheckIcon size={16} className="text-green-500 mr-2" data-id="element-1139" />
                      <span className="text-sm text-gray-700" data-id="element-1140">
                        {permission}
                      </span>
                    </div>)}
                </div>
                <div className="mt-4 text-sm text-gray-500" data-id="element-1141">
                  <p data-id="element-1142">
                    These permissions determine what actions you can perform in
                    the system.
                    {!isSuperAdmin() && ' Contact a super administrator if you need additional permissions.'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
      {/* Change Password Modal */}
      {showPasswordModal && <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" data-id="element-1143">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full" data-id="element-1144">
            <div className="px-6 py-4 border-b border-gray-200" data-id="element-1145">
              <h3 className="text-lg font-medium text-gray-900" data-id="element-1146">
                Change Password
              </h3>
            </div>
            <div className="p-6" data-id="element-1147">
              <div className="space-y-4" data-id="element-1148">
                <div data-id="element-1149">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1150">
                    Current Password
                  </label>
                  <div className="relative" data-id="element-1151">
                    <input type={passwordVisibility.currentPassword ? 'text' : 'password'} name="currentPassword" value={passwordValues.currentPassword} onChange={handlePasswordChange} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm" data-id="element-1152" />
                    <button type="button" className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-500" onClick={() => togglePasswordVisibility('currentPassword')} data-id="element-1153">
                      {passwordVisibility.currentPassword ? <EyeOffIcon className="h-5 w-5" data-id="element-1154" /> : <EyeIcon className="h-5 w-5" data-id="element-1155" />}
                    </button>
                  </div>
                </div>
                <div data-id="element-1156">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1157">
                    New Password
                  </label>
                  <div className="relative" data-id="element-1158">
                    <input type={passwordVisibility.newPassword ? 'text' : 'password'} name="newPassword" value={passwordValues.newPassword} onChange={handlePasswordChange} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm" data-id="element-1159" />
                    <button type="button" className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-500" onClick={() => togglePasswordVisibility('newPassword')} data-id="element-1160">
                      {passwordVisibility.newPassword ? <EyeOffIcon className="h-5 w-5" data-id="element-1161" /> : <EyeIcon className="h-5 w-5" data-id="element-1162" />}
                    </button>
                  </div>
                  <p className="mt-1 text-xs text-gray-500" data-id="element-1163">
                    Password must be at least 8 characters and include
                    uppercase, lowercase, number, and special character.
                  </p>
                </div>
                <div data-id="element-1164">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1165">
                    Confirm New Password
                  </label>
                  <div className="relative" data-id="element-1166">
                    <input type={passwordVisibility.confirmPassword ? 'text' : 'password'} name="confirmPassword" value={passwordValues.confirmPassword} onChange={handlePasswordChange} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm" data-id="element-1167" />
                    <button type="button" className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-500" onClick={() => togglePasswordVisibility('confirmPassword')} data-id="element-1168">
                      {passwordVisibility.confirmPassword ? <EyeOffIcon className="h-5 w-5" data-id="element-1169" /> : <EyeIcon className="h-5 w-5" data-id="element-1170" />}
                    </button>
                  </div>
                </div>
                {errorMessage && <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-md" data-id="element-1171">
                    <div className="flex items-center" data-id="element-1172">
                      <XCircleIcon size={16} className="text-red-500 mr-2" data-id="element-1173" />
                      <p className="text-sm text-red-700" data-id="element-1174">{errorMessage}</p>
                    </div>
                  </div>}
              </div>
            </div>
            <div className="px-6 py-3 border-t border-gray-200 flex justify-end space-x-3" data-id="element-1175">
              <button onClick={() => {
            setShowPasswordModal(false);
            setErrorMessage(null);
            setPasswordValues({
              currentPassword: '',
              newPassword: '',
              confirmPassword: ''
            });
          }} className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50" data-id="element-1176">
                Cancel
              </button>
              <button onClick={changePassword} disabled={isSaving} className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700" data-id="element-1177">
                {isSaving ? 'Changing...' : 'Change Password'}
              </button>
            </div>
          </div>
        </div>}
      {/* Two-Factor Authentication Modal */}
      {showTwoFactorModal && <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" data-id="element-1178">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full" data-id="element-1179">
            <div className="px-6 py-4 border-b border-gray-200" data-id="element-1180">
              <h3 className="text-lg font-medium text-gray-900" data-id="element-1181">
                {profile?.twoFactorEnabled ? 'Disable' : 'Enable'} Two-Factor
                Authentication
              </h3>
            </div>
            <div className="p-6" data-id="element-1182">
              {profile?.twoFactorEnabled ? <div className="space-y-4" data-id="element-1183">
                  <div className="flex items-center bg-yellow-50 p-4 rounded-md" data-id="element-1184">
                    <AlertCircleIcon size={20} className="text-yellow-600 mr-3" data-id="element-1185" />
                    <div data-id="element-1186">
                      <h4 className="text-sm font-medium text-yellow-800" data-id="element-1187">
                        Warning
                      </h4>
                      <p className="text-sm text-yellow-700 mt-1" data-id="element-1188">
                        Disabling two-factor authentication will make your
                        account less secure. Are you sure you want to continue?
                      </p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-500" data-id="element-1189">
                    Without two-factor authentication, you'll only need your
                    password to sign in. This is less secure than using a second
                    factor.
                  </p>
                </div> : <div className="space-y-4" data-id="element-1190">
                  <div className="flex items-center bg-green-50 p-4 rounded-md" data-id="element-1191">
                    <ShieldIcon size={20} className="text-green-600 mr-3" data-id="element-1192" />
                    <div data-id="element-1193">
                      <h4 className="text-sm font-medium text-green-800" data-id="element-1194">
                        Enhanced Security
                      </h4>
                      <p className="text-sm text-green-700 mt-1" data-id="element-1195">
                        Two-factor authentication adds an extra layer of
                        security to your account.
                      </p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-500" data-id="element-1196">
                    When enabled, you'll need to provide a verification code
                    from your mobile device in addition to your password when
                    signing in.
                  </p>
                  <div className="mt-4" data-id="element-1197">
                    <h4 className="text-sm font-medium text-gray-900 mb-2" data-id="element-1198">
                      Setup Process:
                    </h4>
                    <ol className="list-decimal pl-5 text-sm text-gray-600 space-y-2" data-id="element-1199">
                      <li data-id="element-1200">
                        Download an authenticator app like Google Authenticator
                        or Microsoft Authenticator
                      </li>
                      <li data-id="element-1201">
                        Scan the QR code that will be displayed after you click
                        "Enable"
                      </li>
                      <li data-id="element-1202">
                        Enter the verification code from the app to confirm
                        setup
                      </li>
                    </ol>
                  </div>
                </div>}
            </div>
            <div className="px-6 py-3 border-t border-gray-200 flex justify-end space-x-3" data-id="element-1203">
              <button onClick={() => setShowTwoFactorModal(false)} className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50" data-id="element-1204">
                Cancel
              </button>
              <button onClick={() => toggleTwoFactor(!profile?.twoFactorEnabled)} disabled={isSaving} className={`px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${profile?.twoFactorEnabled ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}`} data-id="element-1205">
                {isSaving ? 'Processing...' : profile?.twoFactorEnabled ? 'Disable Two-Factor' : 'Enable Two-Factor'}
              </button>
            </div>
          </div>
        </div>}
      {/* Profile Image Upload Modal */}
      {showImageUploadModal && <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" data-id="element-1206">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full" data-id="element-1207">
            <div className="px-6 py-4 border-b border-gray-200" data-id="element-1208">
              <h3 className="text-lg font-medium text-gray-900" data-id="element-1209">
                Update Profile Picture
              </h3>
            </div>
            <div className="p-6" data-id="element-1210">
              <div className="flex flex-col items-center space-y-4" data-id="element-1211">
                <div className="w-32 h-32 relative rounded-full overflow-hidden border-2 border-gray-200" data-id="element-1212">
                  <img src={previewUrl || profile?.profileImage} alt="Profile" className="w-full h-full object-cover" data-id="element-1213" />
                </div>
                <label className="cursor-pointer bg-gray-50 text-gray-700 px-4 py-2 rounded-md border border-gray-300 hover:bg-gray-100 flex items-center" data-id="element-1214">
                  <UploadIcon size={16} className="mr-2" data-id="element-1215" />
                  <span data-id="element-1216">Choose Image</span>
                  <input type="file" className="hidden" accept="image/*" onChange={handleProfileImageChange} data-id="element-1217" />
                </label>
                <p className="text-xs text-gray-500" data-id="element-1218">
                  Recommended: Square image, at least 200x200 pixels. Max size:
                  2MB.
                </p>
              </div>
            </div>
            <div className="px-6 py-3 border-t border-gray-200 flex justify-end space-x-3" data-id="element-1219">
              <button onClick={() => {
            setShowImageUploadModal(false);
            setNewProfileImage(null);
            setPreviewUrl(null);
          }} className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50" data-id="element-1220">
                Cancel
              </button>
              <button onClick={uploadProfileImage} disabled={isSaving || !newProfileImage} className={`px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 ${!newProfileImage ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-1221">
                {isSaving ? 'Uploading...' : 'Upload Image'}
              </button>
            </div>
          </div>
        </div>}
      {/* Delete Account Modal */}
      {showDeleteAccountModal && <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" data-id="element-1222">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full" data-id="element-1223">
            <div className="px-6 py-4 border-b border-gray-200" data-id="element-1224">
              <h3 className="text-lg font-medium text-red-600" data-id="element-1225">
                Delete Account
              </h3>
            </div>
            <div className="p-6" data-id="element-1226">
              <div className="flex items-center bg-red-50 p-4 rounded-md mb-4" data-id="element-1227">
                <AlertCircleIcon size={20} className="text-red-600 mr-3" data-id="element-1228" />
                <div data-id="element-1229">
                  <h4 className="text-sm font-medium text-red-800" data-id="element-1230">
                    Warning: This action cannot be undone
                  </h4>
                  <p className="text-sm text-red-700 mt-1" data-id="element-1231">
                    Deleting your account will permanently remove all your data
                    from our system.
                  </p>
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-4" data-id="element-1232">
                Please type <strong data-id="element-1233">DELETE</strong> to confirm that you want to
                permanently delete your account:
              </p>
              <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm" placeholder="Type DELETE to confirm" data-id="element-1234" />
            </div>
            <div className="px-6 py-3 border-t border-gray-200 flex justify-end space-x-3" data-id="element-1235">
              <button onClick={() => setShowDeleteAccountModal(false)} className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50" data-id="element-1236">
                Cancel
              </button>
              <button className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 opacity-50 cursor-not-allowed" disabled data-id="element-1237">
                Delete Account
              </button>
            </div>
          </div>
        </div>}
      {/* Logout Confirmation Modal */}
      {showLogoutModal && <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" data-id="element-1238">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full" data-id="element-1239">
            <div className="px-6 py-4 border-b border-gray-200" data-id="element-1240">
              <h3 className="text-lg font-medium text-gray-900" data-id="element-1241">
                Confirm Logout
              </h3>
            </div>
            <div className="p-6" data-id="element-1242">
              <p className="text-sm text-gray-600" data-id="element-1243">
                Are you sure you want to log out of your account?
              </p>
            </div>
            <div className="px-6 py-3 border-t border-gray-200 flex justify-end space-x-3" data-id="element-1244">
              <button onClick={() => setShowLogoutModal(false)} className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50" data-id="element-1245">
                Cancel
              </button>
              <button onClick={handleLogout} className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700" data-id="element-1246">
                Logout
              </button>
            </div>
          </div>
        </div>}
    </div>;
};
export default AdminProfile;